import { Fragment, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { fDateTime } from 'src/utils/format-time';
import {
  stErrorMessage,
  stRedPacketReclaimHistory,
  StRedPacketReclaimHistoryItem,
} from '../../../services/socialTradingService';

const PAGE_SIZE = 20;

/** Reclaim runs from audit_log. Remount (change `key`) to reload from page 1. */
export default function ReclaimHistory() {
  const t = useTranslate();
  const notify = useNotify();
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<StRedPacketReclaimHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    stRedPacketReclaimHistory({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((res) => {
        if (stale) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e) => {
        if (!stale) notify(stErrorMessage(e), { type: 'error' });
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [page, notify]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('module.socialTrading.reclaimHistory')}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('module.socialTrading.reclaimHistoryColTime')}</TableCell>
              <TableCell>{t('module.socialTrading.reclaimHistoryColOperator')}</TableCell>
              <TableCell>{t('module.socialTrading.reclaimReason')}</TableCell>
              <TableCell align="right">{t('module.socialTrading.reclaimHistoryColUsers')}</TableCell>
              <TableCell align="right">{t('module.socialTrading.reclaimColAmount')}</TableCell>
              <TableCell>{t('module.socialTrading.reclaimHistoryColBatch')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.socialTrading.reclaimHistoryEmpty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map((it) => {
              const open = openId === it.id;
              return (
                <Fragment key={it.id}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      <IconButton size="small" onClick={() => setOpenId(open ? null : it.id)}>
                        {open ? '▾' : '▸'}
                      </IconButton>
                    </TableCell>
                    <TableCell>{fDateTime(it.createdAt)}</TableCell>
                    <TableCell>{it.operator}</TableCell>
                    <TableCell>{it.reason}</TableCell>
                    <TableCell align="right">{it.entries.length}</TableCell>
                    <TableCell align="right">
                      {it.totalReclaimed}
                      {it.result !== 'SUCCESS' && (
                        <Chip size="small" color="error" sx={{ ml: 1 }} label={t(`module.socialTrading.result${it.result}`)} />
                      )}
                    </TableCell>
                    <TableCell>{it.batchId}</TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={6}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('module.socialTrading.reclaimColUserId')}</TableCell>
                              <TableCell align="right">{t('module.socialTrading.reclaimColAmount')}</TableCell>
                              <TableCell align="right">{t('module.socialTrading.reclaimColBalance')}</TableCell>
                              <TableCell align="right">{t('module.socialTrading.reclaimColCancelled')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {it.entries.map((e) => (
                              <TableRow key={e.userId}>
                                <TableCell>{e.userId}</TableCell>
                                <TableCell align="right">{e.amount}</TableCell>
                                <TableCell align="right">{e.balanceAmount}</TableCell>
                                <TableCell align="right">{e.cancelledWithdrawalCount}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, p) => setPage(p)}
        />
      </CardContent>
    </Card>
  );
}
